<template>
  <div class="flex-1 w-full relative p-4">
    <Line
      :data="chartData"
      :options="chartOptions"
      :chart-id="chartId"
      :dataset-id-key="datasetIdKey"
      :plugins="plugins"
      :css-classes="cssClasses"
      :styles="styles"
      :width="width"
      :height="height"
    />
  </div>
</template>

<script setup lang="ts">
import { australianCurrency } from '@/helpers';
import { t } from '@/i18n';
import Project from '@/models/Project';
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import { DateTime } from 'luxon';
import { computed } from 'vue';
import { Line } from 'vue-chartjs';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

const props = defineProps<{
  project: Project;
  forecastCumulative: number[];
  monthlyCertifiedCumulative: number[];
  baselineCumulative: number[];
  extendedChartMonths: DateTime[];
}>();

const emit = defineEmits<{
  hover: [index: number | undefined];
}>();

// Chart configuration
const chartId = 'cashflow-line-chart';
const datasetIdKey = 'label';
const plugins = [];
const cssClasses = '';
const styles = {};
const width = undefined;
const height = undefined;

const chartData = computed(() => {
  const labels = props.extendedChartMonths.map((month) => {
    return month.toFormat('MMM-yyyy');
  });

  return {
    labels,
    datasets: [
      {
        label: t('HeadContract.CashFlow.forecast'),
        data: props.forecastCumulative,
        fill: false,
        borderColor: '#3B82F6',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        borderWidth: 3,
        pointBackgroundColor: '#3B82F6',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
        tension: 0.4,
        order: 2,
      },
      {
        label: t('HeadContract.CashFlow.actual'),
        data: props.monthlyCertifiedCumulative,
        fill: false,
        borderColor: '#F59E0B',
        backgroundColor: 'rgba(245, 158, 11, 0.1)',
        borderWidth: 3,
        pointBackgroundColor: '#F59E0B',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
        tension: 0.4,
        order: 1,
      },
      {
        label: t('HeadContract.CashFlow.baseline'),
        data: props.baselineCumulative,
        fill: false,
        borderColor: '#EF4444',
        backgroundColor: 'rgba(239, 68, 68, 0.1)',
        borderWidth: 3,
        pointBackgroundColor: '#EF4444',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
        tension: 0.4,
        order: 3,
      },
    ],
  };
});

const chartOptions = computed(() => ({
  responsive: true,
  plugins: {
    legend: {
      display: false, // We have custom legend in the header
    },

    title: {
      display: false,
    },

    tooltip: {
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      titleColor: '#ffffff',
      bodyColor: '#ffffff',
      borderColor: '#e5e7eb',
      borderWidth: 1,
      cornerRadius: 8,
      displayColors: true,
      callbacks: {
        label(context: any) {
          return context.dataset.label + ': ' + australianCurrency(context.parsed.y);
        },
      },
    },
  },

  maintainAspectRatio: false,
  scales: {
    x: {
      grid: {
        color: '#f3f4f6',
      },

      ticks: {
        color: '#6b7280',
        font: {
          size: 12,
        },
      },
    },

    y: {
      grid: {
        color: '#f3f4f6',
      },

      ticks: {
        color: '#6b7280',
        font: {
          size: 12,
        },

        callback(value: any) {
          return australianCurrency(Number(value));
        },
      },
    },
  },

  interaction: {
    intersect: false,
    mode: 'index' as const,
  },

  onHover: (event: any, activeElements: any[]) => {
    if(activeElements.length > 0) {
      const index = activeElements[0].index;

      emit('hover', index);
    } else {
      emit('hover', undefined);
    }
  },
}));
</script>

<style scoped></style>
